import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import AddCroppedImageForm from "@/components/images/AddCroppedImageForm";
import {
  useGetAllAttachments,
  useGetAttachmentById,
  type Attachment,
} from "@/api/attachment";
import { BoElementImagePickerBody } from "@/components/cms-puck/fields/BoElementImagePicker";
import {
  closePuckDrawer,
  openPuckDrawer,
} from "@/components/cms-puck/fields/puck-drawer-store";
import { normalizeAttachmentIds } from "@/lib/cms-puck/blocks/gallery";
import { ChevronDown, ChevronUp, Wand2, XIcon } from "lucide-react";
import { openImageStudio } from "@/components/cms-puck/fields/image-studio-store";
import { canRetouchAttachment } from "@/components/images/can-retouch-attachment";
import { useAuth } from "@/context/auth/AuthContext";
import { stripExtension } from "@/lib/utils";
import { AttachmentPixelSize } from "@/components/cms-puck/fields/AttachmentPixelSize";
import {
  attachmentThumb,
  attachmentTitle,
  cmsMediaLibraryWhere,
  type CatalogMediaKind,
} from "@/lib/cms-puck/media-utils";
import { Button } from "@/components/ui/button";
import SearchBar from "@/components/ui/searchbar";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";

type GallerySourceTab = "library" | "upload" | "catalog";

const ALL_CATALOG_MEDIA_KINDS: CatalogMediaKind[] = ["image", "video", "embed"];
const CMS_PUCK_MEDIA_POSITION = "cmsPuckMedia";

function thumbUrl(att: Attachment | undefined | null): string | undefined {
  return attachmentThumb(att);
}

function MediaThumb({
  att,
  size = 56,
}: {
  att: Attachment | undefined | null;
  size?: number;
}) {
  const url = thumbUrl(att);
  // `shrink-0` : évite l'écrasement de la vignette dans les layouts flex étroits.
  const boxStyle = { width: size, height: size, minWidth: size } as const;
  if (!url) {
    return (
      <div
        className="flex shrink-0 items-center justify-center rounded bg-slate-100 text-[10px] text-slate-400"
        style={boxStyle}
      >
        {att?.kind === "embed" ? "embed" : att?.kind === "video" ? "vidéo" : "—"}
      </div>
    );
  }
  return (
    <div
      className="flex shrink-0 items-center justify-center overflow-hidden rounded bg-slate-100"
      style={boxStyle}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={url}
        alt={att?.name ?? ""}
        className="h-full w-full object-contain"
      />
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? "bg-white text-slate-900 shadow-sm"
          : "text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}

function GalleryUploadTab({
  cmsId,
  onUploaded,
}: {
  cmsId: string | undefined;
  onUploaded: (id: string) => void;
}) {
  const cmsNew = useCmsNew();
  const queryClient = useQueryClient();

  if (!cmsId) {
    return (
      <p className="text-sm text-slate-500">
        {cmsNew(
          "cms.new.pickers.gallery.cmsNotFound",
          "CMS introuvable pour cette instance.",
        )}
      </p>
    );
  }

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ["artworks", "attachments"] });
  };

  return (
    <AddCroppedImageForm
      docId={cmsId}
      imagePosition={CMS_PUCK_MEDIA_POSITION}
      refetch={refetch}
      forceCreateNewAttachment
      onSuccess={(created) => {
        if (created.id) onUploaded(created.id);
      }}
      buildImageName={(_instanceName, _docId, _position, _data, file) =>
        file ? stripExtension(file.name) : "embed"
      }
    />
  );
}

function GalleryPickerBody({
  initialIds,
  onChange,
  allowedCatalogKinds = ALL_CATALOG_MEDIA_KINDS,
}: {
  initialIds: string[];
  onChange: (ids: string[]) => void;
  allowedCatalogKinds?: CatalogMediaKind[];
}) {
  const cmsNew = useCmsNew();
  const [ids, setIds] = useState<string[]>(initialIds);
  const selected = useMemo(() => new Set(ids), [ids]);
  const [sourceTab, setSourceTab] = useState<GallerySourceTab>("library");
  const [search, setSearch] = useState("");
  const { instanceData } = useAuth();
  const cmsId = instanceData?.CMS?.id;
  const instanceId = instanceData?.id;

  const { data: library = [], isLoading } = useGetAllAttachments({
    where: cmsMediaLibraryWhere(cmsId, instanceId),
    order: { order: "asc" },
  });

  const filteredLibrary = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return library;
    return library.filter((att) =>
      attachmentTitle(att).toLowerCase().includes(q),
    );
  }, [library, search]);

  const applyChange = (next: string[]) => {
    setIds(next);
    onChange(next);
  };

  const toggle = (id: string) => {
    const next = selected.has(id)
      ? ids.filter((x) => x !== id)
      : [...ids, id];
    applyChange(next);
  };

  const move = (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    const next = [...ids];
    [next[index], next[j]] = [next[j], next[index]];
    applyChange(next);
  };

  return (
    <div className="flex max-h-[70vh] flex-col gap-4 overflow-hidden pt-4">
      {ids.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-slate-600">
            {cmsNew("cms.new.auto.text.selection_ordre_affichage", "Sélection ({count}) — ordre d'affichage").replace("{count}", String(ids.length))}
          </p>
          <ul className="max-h-40 space-y-1 overflow-y-auto rounded border border-slate-200 bg-slate-50 p-2">
            {ids.map((id, index) => (
              <SelectedRow
                key={id}
                id={id}
                index={index}
                total={ids.length}
                onRemove={() => toggle(id)}
                onMoveUp={() => move(index, -1)}
                onMoveDown={() => move(index, 1)}
              />
            ))}
          </ul>
        </div>
      )}

      <div className="inline-flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1">
        <TabButton
          active={sourceTab === "library"}
          onClick={() => setSourceTab("library")}
        >
          {cmsNew("cms.new.auto.text.bibliotheque", "Bibliothèque")}
        </TabButton>
        <TabButton
          active={sourceTab === "upload"}
          onClick={() => setSourceTab("upload")}
        >
          {cmsNew("cms.new.auto.text.ajouter_un_media", "Ajouter un média")}
        </TabButton>
        <TabButton
          active={sourceTab === "catalog"}
          onClick={() => setSourceTab("catalog")}
        >
          {cmsNew("cms.new.auto.text.catalogue", "Catalogue")}
        </TabButton>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {sourceTab === "library" ? (
          <>
            <SearchBar
              search={search}
              onChangeSearch={setSearch}
              placeholder={cmsNew("cms.new.auto.placeholder.rechercher_dans_la_bibliotheque", "Rechercher dans la bibliothèque…")}
            />
            {isLoading ? (
              <p className="mt-2 text-sm text-slate-400">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
            ) : library.length === 0 ? (
              <p className="mt-2 text-sm text-slate-400">
                {cmsPhrase(cmsNew, "galleryEmptyLibrary")}
              </p>
            ) : filteredLibrary.length === 0 ? (
              <p className="mt-2 text-sm text-slate-400">{cmsNew("cms.new.auto.text.aucun_resultat", "Aucun résultat.")}</p>
            ) : (
              <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
                {filteredLibrary.map((att) => {
                  const active = selected.has(att.id);
                  return (
                    <button
                      key={att.id}
                      type="button"
                      onClick={() => toggle(att.id)}
                      className={`rounded border p-1 text-left transition-colors ${
                        active
                          ? "border-primary ring-2 ring-primary/30"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <MediaThumb att={att} size={72} />
                      <p className="mt-1 line-clamp-2 text-[10px] text-slate-600">
                        {attachmentTitle(att)}
                      </p>
                      <AttachmentPixelSize
                        att={att}
                        className="line-clamp-1 text-[9px] text-slate-400"
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </>
        ) : sourceTab === "upload" ? (
          <GalleryUploadTab
            cmsId={cmsId}
            onUploaded={(id) => {
              toggle(id);
              setSourceTab("library");
            }}
          />
        ) : (
          <BoElementImagePickerBody
            multiSelect
            selectedIds={selected}
            onToggle={toggle}
            allowedKinds={allowedCatalogKinds}
          />
        )}
      </div>

      <div className="flex justify-end border-t border-slate-100 pt-3">
        <Button type="button" size="sm" onClick={() => closePuckDrawer()}>
          {cmsPhrase(cmsNew, "galleryDone")}
        </Button>
      </div>
    </div>
  );
}

function SelectedRow({
  id,
  index,
  total,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  id: string;
  index: number;
  total: number;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const cmsNew = useCmsNew();
  const { data } = useGetAttachmentById(id);
  const att = data as Attachment | undefined;
  return (
    <li className="flex items-center gap-2 rounded bg-white px-2 py-1">
      <MediaThumb att={att} size={36} />
      <span className="min-w-0 flex-1 truncate text-xs text-slate-600">
        {att ? attachmentTitle(att) : id.slice(0, 8)}
      </span>
      <button
        type="button"
        className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
        disabled={index === 0}
        onClick={onMoveUp}
        aria-label={cmsNew("cms.new.auto.label.monter", "Monter")}
      >
        <ChevronUp size={14} />
      </button>
      <button
        type="button"
        className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
        disabled={index >= total - 1}
        onClick={onMoveDown}
        aria-label={cmsNew("cms.new.auto.label.descendre", "Descendre")}
      >
        <ChevronDown size={14} />
      </button>
      {att && canRetouchAttachment(att) && (
        <button
          type="button"
          className="text-slate-400 hover:text-slate-700"
          onClick={() => openImageStudio(att)}
          aria-label={cmsNew("cms.new.pickers.media.retouch", "Retoucher")}
        >
          <Wand2 size={14} />
        </button>
      )}
      <button
        type="button"
        className="text-slate-400 hover:text-red-600"
        onClick={onRemove}
        aria-label={cmsNew("cms.new.auto.label.retirer", "Retirer")}
      >
        <XIcon size={14} />
      </button>
    </li>
  );
}

/**
 * Champ Puck : sélection multiple de médias (médiathèque CMS + catalogue BO)
 * pour Galerie/Carousel.
 */
export function GalleryPickerField({
  value,
  onChange,
  allowedCatalogKinds,
}: {
  value: string[] | undefined;
  onChange: (value: string[]) => void;
  /** Kinds autorisés dans l'onglet Catalogue (défaut : image + vidéo + embed). */
  allowedCatalogKinds?: CatalogMediaKind[];
}) {
  const cmsNew = useCmsNew();
  const ids = normalizeAttachmentIds(value);

  return (
    <div className="space-y-2 p-1">
      <div className="flex flex-wrap gap-2">
        {ids.slice(0, 6).map((id) => (
          <GalleryThumb key={id} id={id} />
        ))}
        {ids.length > 6 && (
          <span className="self-center text-xs text-slate-400">
            {cmsPhrase(cmsNew, "galleryMore").replace("{count}", String(ids.length - 6))}
          </span>
        )}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() =>
          openPuckDrawer({
            title: cmsNew("cms.new.auto.title.medias_de_la_galerie", "Médias de la galerie"),
            maxWidth: "lg",
            node: (
              <GalleryPickerBody
                initialIds={ids}
                onChange={(next) => onChange(next)}
                allowedCatalogKinds={allowedCatalogKinds}
              />
            ),
          })
        }
      >
        {ids.length ? cmsPhrase(cmsNew, "galleryEditMedia").replace("{count}", String(ids.length)) : cmsPhrase(cmsNew, "galleryChooseMedia")}
      </Button>
    </div>
  );
}

function GalleryThumb({ id }: { id: string }) {
  const { data } = useGetAttachmentById(id);
  return <MediaThumb att={data as Attachment | undefined} size={48} />;
}
